import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  computed,
  inject,
  input,
  type Signal,
} from '@angular/core';
import { NgxEchartsDirective } from 'ngx-echarts';
import type { EChartsOption } from 'echarts';
import { buildEchartsTheme } from '../echarts-theme';

export interface IEchartsFunnelDatum {
  name: string;
  value: number;
}

@Component({
  selector: 'pr-echarts-funnel',
  templateUrl: './echarts-funnel.component.html',
  styleUrls: ['./echarts-funnel.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgxEchartsDirective],
})
export class EchartsFunnelComponent {
  private readonly _elementRef = inject<ElementRef<HTMLElement>>(ElementRef);

  readonly data = input<IEchartsFunnelDatum[]>([]);
  readonly label = input.required<string>();
  readonly sortMode = input<'descending' | 'ascending' | 'none'>('descending');
  readonly height = input<number>(280);
  readonly ariaLabelOverride = input<string>();

  readonly hasData = computed(() => this.data().length > 0);

  readonly ariaLabel = computed(() => {
    const override = this.ariaLabelOverride();
    if (override) {
      return override;
    }
    const stages = this.data();
    if (stages.length === 0) {
      return this.label();
    }
    const summary = stages
      .map((stage) => `${stage.name}: ${stage.value}`)
      .join(', ');
    return `${this.label()} funnel chart, ${stages.length} stages, ${summary}`;
  });

  readonly chartOptions: Signal<EChartsOption> = computed(() => {
    const stages = this.data();
    if (stages.length === 0) {
      return {};
    }

    const theme = buildEchartsTheme(this._elementRef.nativeElement);

    return {
      ...theme,
      tooltip: {
        ...theme.tooltip,
        trigger: 'item',
        formatter: '{b}: {c}',
      },
      legend: {
        ...theme.legend,
        show: false,
      },
      series: [
        {
          type: 'funnel',
          name: this.label(),
          sort: this.sortMode(),
          left: '10%',
          width: '80%',
          minSize: '20%',
          maxSize: '100%',
          gap: 2,
          label: {
            show: true,
            position: 'inside',
            formatter: '{b}\n{c}',
            fontSize: 12,
          },
          emphasis: {
            label: {
              fontSize: 14,
            },
          },
          data: stages.map((stage) => ({
            name: stage.name,
            value: stage.value,
          })),
        },
      ],
    };
  });
}
